import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import AuthService from "../../services/auth.service";
import UserService from "../../services/user.service";
import { BusinessContext } from "../../context/BusinessContext";
import LogoComponent from "../logo";
import {
  HiOutlineCube,
  HiPlus,
  HiCloudArrowUp,
  HiMapPin,
  HiCog6Tooth,
  HiArrowLeft,
  HiArrowRightOnRectangle,
  HiBars3,
  HiChevronUpDown,
} from "react-icons/hi2";

// ─── Nav items ────────────────────────────────────────────────────────────────

const NAV = [
  {
    to: "/business/dashboard",
    end: true,
    label: "Orders",
    icon: <HiOutlineCube className="h-5 w-5" />,
  },
  {
    to: "/business/dashboard/create",
    label: "New Order",
    icon: <HiPlus className="h-5 w-5" />,
  },
  {
    to: "/business/dashboard/bulk",
    label: "Bulk Upload",
    icon: <HiCloudArrowUp className="h-5 w-5" />,
  },
  {
    to: "/business/dashboard/track",
    label: "Track Parcel",
    icon: <HiMapPin className="h-5 w-5" />,
  },
  {
    to: "/business/dashboard/settings",
    label: "Settings",
    icon: <HiCog6Tooth className="h-5 w-5" />,
  },
];

// ─── Sidebar content (shared between desktop + mobile drawer) ────────────────

function SidebarContent({ onNavClick }) {
  const navigate = useNavigate();
  const user = AuthService.getCurrentUser()?.user;

  function handleLogout() {
    AuthService.logout();
    navigate("/login");
  }

  return (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-5 py-5 border-b border-white/10 bg-white">
        <Link to="/" className="flex flex-col gap-1">
          <LogoComponent placement="footer" />
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                isActive
                  ? "bg-white/15 text-white"
                  : "text-white/60 hover:text-white hover:bg-white/8"
              }`
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* User + logout */}
      <div className="px-3 py-4 border-t border-white/10 space-y-1">
        <Link
          to="/"
          className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-white/50 hover:text-white hover:bg-white/8 transition-colors text-xs font-medium"
        >
          <HiArrowLeft className="h-4 w-4" />
          Back to main site
        </Link>

        <div className="flex items-center gap-3 px-3 py-2 mt-1">
          {user?.profile_pic_url ? (
            <img
              src={user.profile_pic_url}
              alt=""
              className="h-7 w-7 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">
                {(user?.first_name?.[0] ?? "?").toUpperCase()}
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-white text-xs font-semibold truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-white/40 text-[10px] truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            title="Sign out"
            className="text-white/40 hover:text-red-400 transition-colors flex-shrink-0"
          >
            <HiArrowRightOnRectangle className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main layout ─────────────────────────────────────────────────────────────

export default function BusinessLayout() {
  const [businesses, setBusinesses] = useState(
    AuthService.getCurrentUser()?.user?.businesses ?? [],
  );
  const [selectedBusiness, setSelectedBusiness] = useState(
    (AuthService.getCurrentUser()?.user?.businesses ?? [])[0] ?? null,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    UserService.getUserBoard()
      .then((response) => {
        const user = response.data?.user ?? response.data;
        const list = user?.businesses ?? [];
        if (list.length === 0) return;
        setBusinesses(list);
        setSelectedBusiness((prev) =>
          prev ? list.find((b) => b.id === prev.id) ?? list[0] : list[0],
        );
      })
      .catch(() => {});
  }, []);

  return (
    <BusinessContext.Provider value={{ selectedBusiness, setSelectedBusiness, businesses }}>
      <div className="flex h-screen overflow-hidden bg-gray-50">
        {/* ── Desktop sidebar ── */}
        <aside className="hidden lg:flex flex-col w-60 flex-shrink-0 bg-gradient-to-b from-violet-700 to-violet-900">
          <SidebarContent />
        </aside>

        {/* ── Mobile drawer overlay ── */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={() => setDrawerOpen(false)}
          />
        )}

        {/* ── Mobile drawer ── */}
        <aside
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-violet-700 to-violet-900 flex flex-col transform transition-transform duration-300 ease-in-out lg:hidden ${
            drawerOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <SidebarContent onNavClick={() => setDrawerOpen(false)} />
        </aside>

        {/* ── Main column ── */}
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          {/* Top header */}
          <header className="flex items-center justify-between px-4 lg:px-6 py-3 bg-white border-b border-gray-100 flex-shrink-0 gap-3">
            {/* Hamburger (mobile) */}
            <button
              className="lg:hidden p-2 -ml-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors flex-shrink-0"
              onClick={() => setDrawerOpen(true)}
            >
              <HiBars3 className="h-5 w-5" />
            </button>

            {/* Business selector */}
            {businesses.length === 1 ? (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-50 border border-gray-100 flex-1 min-w-0 max-w-xs">
                <div className="h-2 w-2 rounded-full bg-green-400 flex-shrink-0" />
                <span className="text-sm font-semibold text-gray-700 truncate">
                  {selectedBusiness?.name}
                </span>
              </div>
            ) : (
              <div className="relative flex items-center flex-1 min-w-0 max-w-xs">
                <div className="h-2 w-2 rounded-full bg-green-400 flex-shrink-0 absolute left-3 z-10 pointer-events-none" />
                <select
                  value={selectedBusiness?.id ?? ""}
                  onChange={(e) => {
                    const biz = businesses.find((b) => b.id.toString() === e.target.value);
                    if (biz) setSelectedBusiness(biz);
                  }}
                  className="w-full pl-7 pr-9 py-1.5 text-sm font-semibold text-gray-800 bg-violet-50 border border-violet-200 rounded-xl cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-violet-400 hover:bg-violet-100 hover:border-violet-300 transition-colors appearance-none"
                >
                  {businesses.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                {/* Custom chevron */}
                <HiChevronUpDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-violet-500 pointer-events-none" />
              </div>
            )}

            {/* New Order CTA */}
            <NavLink
              to="/business/dashboard/create"
              className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
            >
              <HiPlus className="h-3.5 w-3.5" />
              New Order
            </NavLink>
          </header>

          {/* Scrollable content — key forces full remount on business switch */}
          <main key={selectedBusiness?.id} className="flex-1 overflow-y-auto">
            <Outlet />
          </main>

          {/* Footer */}
          <footer className="flex-shrink-0 border-t border-gray-100 bg-white px-6 py-3 flex items-center justify-between">
            <p className="text-xs text-gray-400">© {new Date().getFullYear()} Parcelin · All rights reserved</p>
            <div className="flex items-center gap-4">
              <a href="/contact" className="text-xs text-gray-400 hover:text-violet-600 transition-colors">Help</a>
              <a href="/terms"   className="text-xs text-gray-400 hover:text-violet-600 transition-colors">Terms</a>
              <a href="/privacy" className="text-xs text-gray-400 hover:text-violet-600 transition-colors">Privacy</a>
            </div>
          </footer>
        </div>
      </div>
    </BusinessContext.Provider>
  );
}
